/**
 * Locales — ICU BCP 47 codes với number + date format example.
 *
 * Dùng trong:
 * - ProjectDetail tab Cấu hình (dropdown locale)
 * - Backend translate.ts inject vào prompt: "Use {locale} locale for numbers and dates (e.g. {number}; {date})."
 *
 * Empty string ('') → auto-derive từ targetLang (xem getDefaultLocale).
 */

export interface Locale {
  /** BCP 47 code (vd 'en-US', 'vi-VN') */
  code: string;
  /** Hiển thị trong dropdown */
  label: string;
  /** Format example: number + date */
  example: string;
  /** Instruction text inject vào prompt */
  instruction: string;
}

export const LOCALES: Locale[] = [
  // ── English variants ──
  { code: 'en-US', label: 'English (US)',     example: '1,000.00 · 12/31/2024',  instruction: 'Use en-US locale for numbers and dates (e.g. 1,000.00; 12/31/2024).' },
  { code: 'en-GB', label: 'English (UK)',     example: '1,000.00 · 31/12/2024',  instruction: 'Use en-GB locale for numbers and dates (e.g. 1,000.00; 31/12/2024).' },
  { code: 'en-AU', label: 'English (AU)',     example: '1,000.00 · 31/12/2024',  instruction: 'Use en-AU locale for numbers and dates (e.g. 1,000.00; 31/12/2024).' },
  { code: 'en-CA', label: 'English (Canada)', example: '1,000.00 · 2024-12-31',  instruction: 'Use en-CA locale for numbers and dates (e.g. 1,000.00; 2024-12-31).' },
  { code: 'en-IN', label: 'English (India)',  example: '1,00,000.00 · 31/12/2024', instruction: 'Use en-IN locale with Indian number grouping (e.g. 1,00,000.00; 31/12/2024).' },

  // ── Vietnamese ──
  { code: 'vi-VN', label: 'Tiếng Việt',       example: '1.000,00 · 31/12/2024',  instruction: 'Use vi-VN locale for numbers and dates (e.g. 1.000,00; 31/12/2024).' },

  // ── East Asian ──
  { code: 'ja-JP', label: '日本語',             example: '1,000.00 · 2024/12/31',  instruction: 'Use ja-JP locale for numbers and dates (e.g. 1,000.00; 2024/12/31).' },
  { code: 'ko-KR', label: '한국어',             example: '1,000.00 · 2024.12.31',  instruction: 'Use ko-KR locale for numbers and dates (e.g. 1,000.00; 2024.12.31).' },
  { code: 'zh-CN', label: '中文 (简体)',         example: '1,000.00 · 2024/12/31',  instruction: 'Use zh-CN locale for numbers and dates (e.g. 1,000.00; 2024/12/31).' },
  { code: 'zh-TW', label: '中文 (繁體)',         example: '1,000.00 · 2024/12/31',  instruction: 'Use zh-TW locale for numbers and dates (e.g. 1,000.00; 2024/12/31).' },
  { code: 'zh-HK', label: '中文 (香港)',         example: '1,000.00 · 31/12/2024',  instruction: 'Use zh-HK locale for numbers and dates (e.g. 1,000.00; 31/12/2024).' },

  // ── European (Romance) ──
  { code: 'fr-FR', label: 'Français (France)', example: '1 000,00 · 31/12/2024', instruction: 'Use fr-FR locale for numbers and dates (e.g. 1 000,00; 31/12/2024).' },
  { code: 'fr-CA', label: 'Français (Canada)', example: '1 000,00 · 2024-12-31', instruction: 'Use fr-CA locale for numbers and dates (e.g. 1 000,00; 2024-12-31).' },
  { code: 'es-ES', label: 'Español (España)',  example: '1.000,00 · 31/12/2024', instruction: 'Use es-ES locale for numbers and dates (e.g. 1.000,00; 31/12/2024).' },
  { code: 'es-MX', label: 'Español (México)',  example: '1,000.00 · 31/12/2024', instruction: 'Use es-MX locale for numbers and dates (e.g. 1,000.00; 31/12/2024).' },
  { code: 'es-AR', label: 'Español (Argentina)', example: '1.000,00 · 31/12/2024', instruction: 'Use es-AR locale for numbers and dates (e.g. 1.000,00; 31/12/2024).' },
  { code: 'pt-PT', label: 'Português (Portugal)', example: '1.000,00 · 31/12/2024', instruction: 'Use pt-PT locale for numbers and dates (e.g. 1.000,00; 31/12/2024).' },
  { code: 'pt-BR', label: 'Português (Brasil)', example: '1.000,00 · 31/12/2024', instruction: 'Use pt-BR locale for numbers and dates (e.g. 1.000,00; 31/12/2024).' },
  { code: 'it-IT', label: 'Italiano',          example: '1.000,00 · 31/12/2024', instruction: 'Use it-IT locale for numbers and dates (e.g. 1.000,00; 31/12/2024).' },
  { code: 'ro-RO', label: 'Română',            example: '1.000,00 · 31.12.2024', instruction: 'Use ro-RO locale for numbers and dates (e.g. 1.000,00; 31.12.2024).' },
  { code: 'ca-ES', label: 'Català',            example: '1.000,00 · 31/12/2024', instruction: 'Use ca-ES locale for numbers and dates (e.g. 1.000,00; 31/12/2024).' },

  // ── European (Germanic) ──
  { code: 'de-DE', label: 'Deutsch (Deutschland)', example: '1.000,00 · 31.12.2024', instruction: 'Use de-DE locale for numbers and dates (e.g. 1.000,00; 31.12.2024).' },
  { code: 'de-AT', label: 'Deutsch (Österreich)',  example: '1.000,00 · 31.12.2024', instruction: 'Use de-AT locale for numbers and dates (e.g. 1.000,00; 31.12.2024).' },
  { code: 'de-CH', label: 'Deutsch (Schweiz)',     example: "1'000.00 · 31.12.2024", instruction: "Use de-CH locale for numbers and dates (e.g. 1'000.00; 31.12.2024)." },
  { code: 'nl-NL', label: 'Nederlands',            example: '1.000,00 · 31-12-2024', instruction: 'Use nl-NL locale for numbers and dates (e.g. 1.000,00; 31-12-2024).' },
  { code: 'sv-SE', label: 'Svenska',               example: '1 000,00 · 2024-12-31', instruction: 'Use sv-SE locale for numbers and dates (e.g. 1 000,00; 2024-12-31).' },
  { code: 'da-DK', label: 'Dansk',                 example: '1.000,00 · 31.12.2024', instruction: 'Use da-DK locale for numbers and dates (e.g. 1.000,00; 31.12.2024).' },
  { code: 'no-NO', label: 'Norsk',                 example: '1 000,00 · 31.12.2024', instruction: 'Use no-NO locale for numbers and dates (e.g. 1 000,00; 31.12.2024).' },
  { code: 'fi-FI', label: 'Suomi',                 example: '1 000,00 · 31.12.2024', instruction: 'Use fi-FI locale for numbers and dates (e.g. 1 000,00; 31.12.2024).' },
  { code: 'is-IS', label: 'Íslenska',              example: '1.000,00 · 31.12.2024', instruction: 'Use is-IS locale for numbers and dates (e.g. 1.000,00; 31.12.2024).' },

  // ── European (Slavic) ──
  { code: 'ru-RU', label: 'Русский',           example: '1 000,00 · 31.12.2024', instruction: 'Use ru-RU locale for numbers and dates (e.g. 1 000,00; 31.12.2024).' },
  { code: 'pl-PL', label: 'Polski',            example: '1 000,00 · 31.12.2024', instruction: 'Use pl-PL locale for numbers and dates (e.g. 1 000,00; 31.12.2024).' },
  { code: 'uk-UA', label: 'Українська',        example: '1 000,00 · 31.12.2024', instruction: 'Use uk-UA locale for numbers and dates (e.g. 1 000,00; 31.12.2024).' },
  { code: 'cs-CZ', label: 'Čeština',           example: '1 000,00 · 31. 12. 2024', instruction: 'Use cs-CZ locale for numbers and dates (e.g. 1 000,00; 31. 12. 2024).' },
  { code: 'sk-SK', label: 'Slovenčina',        example: '1 000,00 · 31. 12. 2024', instruction: 'Use sk-SK locale for numbers and dates (e.g. 1 000,00; 31. 12. 2024).' },
  { code: 'bg-BG', label: 'Български',         example: '1 000,00 · 31.12.2024',   instruction: 'Use bg-BG locale for numbers and dates (e.g. 1 000,00; 31.12.2024).' },
  { code: 'hr-HR', label: 'Hrvatski',          example: '1.000,00 · 31.12.2024.',  instruction: 'Use hr-HR locale for numbers and dates (e.g. 1.000,00; 31.12.2024.).' },
  { code: 'sr-RS', label: 'Српски',            example: '1.000,00 · 31.12.2024.',  instruction: 'Use sr-RS locale for numbers and dates (e.g. 1.000,00; 31.12.2024.).' },
  { code: 'sl-SI', label: 'Slovenščina',       example: '1.000,00 · 31. 12. 2024', instruction: 'Use sl-SI locale for numbers and dates (e.g. 1.000,00; 31. 12. 2024).' },

  // ── European (Baltic + Other) ──
  { code: 'lt-LT', label: 'Lietuvių',          example: '1 000,00 · 2024-12-31', instruction: 'Use lt-LT locale for numbers and dates (e.g. 1 000,00; 2024-12-31).' },
  { code: 'lv-LV', label: 'Latviešu',          example: '1 000,00 · 31.12.2024', instruction: 'Use lv-LV locale for numbers and dates (e.g. 1 000,00; 31.12.2024).' },
  { code: 'et-EE', label: 'Eesti',             example: '1 000,00 · 31.12.2024', instruction: 'Use et-EE locale for numbers and dates (e.g. 1 000,00; 31.12.2024).' },
  { code: 'hu-HU', label: 'Magyar',            example: '1 000,00 · 2024. 12. 31.', instruction: 'Use hu-HU locale for numbers and dates (e.g. 1 000,00; 2024. 12. 31.).' },
  { code: 'el-GR', label: 'Ελληνικά',          example: '1.000,00 · 31/12/2024', instruction: 'Use el-GR locale for numbers and dates (e.g. 1.000,00; 31/12/2024).' },
  { code: 'tr-TR', label: 'Türkçe',            example: '1.000,00 · 31.12.2024', instruction: 'Use tr-TR locale for numbers and dates (e.g. 1.000,00; 31.12.2024).' },

  // ── Middle Eastern ──
  { code: 'ar-SA', label: 'العربية (السعودية)',  example: '1,000.00 · 31/12/2024', instruction: 'Use ar-SA locale for numbers and dates (e.g. 1,000.00; 31/12/2024). Use Arabic-Indic digits if typical in context.' },
  { code: 'ar-EG', label: 'العربية (مصر)',      example: '1,000.00 · 31/12/2024', instruction: 'Use ar-EG locale for numbers and dates (e.g. 1,000.00; 31/12/2024).' },
  { code: 'he-IL', label: 'עברית',              example: '1,000.00 · 31.12.2024', instruction: 'Use he-IL locale for numbers and dates (e.g. 1,000.00; 31.12.2024).' },
  { code: 'fa-IR', label: 'فارسی',              example: '1,000.00 · 1403/10/10', instruction: 'Use fa-IR locale with Persian calendar support for numbers and dates.' },

  // ── South Asian ──
  { code: 'hi-IN', label: 'हिन्दी',              example: '1,00,000.00 · 31/12/2024', instruction: 'Use hi-IN locale with Indian lakh/crore grouping (e.g. 1,00,000.00; 31/12/2024).' },
  { code: 'bn-IN', label: 'বাংলা (ভারত)',       example: '1,00,000.00 · 31/12/2024', instruction: 'Use bn-IN locale with Indian lakh/crore grouping.' },
  { code: 'ta-IN', label: 'தமிழ்',             example: '1,00,000.00 · 31/12/2024', instruction: 'Use ta-IN locale with Indian number grouping.' },
  { code: 'ur-PK', label: 'اردو',               example: '1,000.00 · 31/12/2024', instruction: 'Use ur-PK locale for numbers and dates (e.g. 1,000.00; 31/12/2024).' },

  // ── Southeast Asian ──
  { code: 'th-TH', label: 'ไทย',                example: '1,000.00 · 31/12/2567', instruction: 'Use th-TH locale. Note: Buddhist calendar year is Gregorian + 543 (e.g. 2024 → 2567).' },
  { code: 'id-ID', label: 'Bahasa Indonesia',   example: '1.000,00 · 31/12/2024', instruction: 'Use id-ID locale for numbers and dates (e.g. 1.000,00; 31/12/2024).' },
  { code: 'ms-MY', label: 'Bahasa Melayu',      example: '1,000.00 · 31/12/2024', instruction: 'Use ms-MY locale for numbers and dates (e.g. 1,000.00; 31/12/2024).' },
  { code: 'tl-PH', label: 'Filipino',           example: '1,000.00 · 12/31/2024', instruction: 'Use tl-PH locale for numbers and dates (e.g. 1,000.00; 12/31/2024).' },
];

/**
 * Map ISO 639-1 (vi/en/ja) → default locale (vi-VN/en-US/ja-JP).
 * Fallback en-US nếu không có entry.
 */
const LANG_TO_LOCALE: Record<string, string> = {
  'vi': 'vi-VN',
  'en': 'en-US',
  'ja': 'ja-JP',
  'ko': 'ko-KR',
  'zh': 'zh-CN',
  'zh-TW': 'zh-TW',
  'fr': 'fr-FR',
  'de': 'de-DE',
  'es': 'es-ES',
  'pt': 'pt-PT',
  'it': 'it-IT',
  'ru': 'ru-RU',
  'pl': 'pl-PL',
  'uk': 'uk-UA',
  'cs': 'cs-CZ',
  'sk': 'sk-SK',
  'nl': 'nl-NL',
  'sv': 'sv-SE',
  'da': 'da-DK',
  'no': 'no-NO',
  'fi': 'fi-FI',
  'is': 'is-IS',
  'hu': 'hu-HU',
  'ro': 'ro-RO',
  'bg': 'bg-BG',
  'hr': 'hr-HR',
  'sr': 'sr-RS',
  'sl': 'sl-SI',
  'lt': 'lt-LT',
  'lv': 'lv-LV',
  'et': 'et-EE',
  'el': 'el-GR',
  'tr': 'tr-TR',
  'ar': 'ar-SA',
  'he': 'he-IL',
  'fa': 'fa-IR',
  'hi': 'hi-IN',
  'bn': 'bn-IN',
  'ta': 'ta-IN',
  'ur': 'ur-PK',
  'th': 'th-TH',
  'id': 'id-ID',
  'ms': 'ms-MY',
  'tl': 'tl-PH',
};

/**
 * Derive default locale từ target language code.
 * VD: getDefaultLocale('ja') → 'ja-JP'
 */
export function getDefaultLocale(targetLangCode: string | undefined): string {
  if (!targetLangCode) return 'en-US';
  return LANG_TO_LOCALE[targetLangCode] || 'en-US';
}

/** Lookup locale by code. */
export function getLocale(code: string | undefined | null): Locale | undefined {
  if (!code) return undefined;
  return LOCALES.find(l => l.code === code);
}

/**
 * Derive country flag emoji from BCP 47 locale code.
 * Uses Unicode regional indicator symbols (RIS) — 2 letters mapped to U+1F1E6..U+1F1FF range.
 * Examples: 'en-US' → 🇺🇸, 'vi-VN' → 🇻🇳, 'ja-JP' → 🇯🇵.
 * Returns empty string if no country segment.
 */
export function flagFromLocale(code: string | undefined | null): string {
  if (!code) return '';
  const parts = code.split('-');
  const country = parts[parts.length - 1];
  if (country.length !== 2) return '';
  const A = 0x1F1E6;
  try {
    const codePoints = country.toUpperCase().split('').map(c => A + c.charCodeAt(0) - 65);
    return String.fromCodePoint(...codePoints);
  } catch {
    return '';
  }
}
